import { Box, Button, TextField, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import RequiredIndicator from '@/components/common/RequiredIndicator';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { Spinner } from '@/components/common/Spinner';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import useSignatures from '@/hooks/useSignature';
import { QuestionProps, SignatureQuestionResponse } from '@/models/Question';
import SavedIndicator from '../SavedIndicator';

export default function Signature({ question }: { question: QuestionProps }) {
  const theme = useTheme();
  const { locale, translatedText } = useContext(LocaleContext);
  const { postSignature, deleteSignatureRelationship } = useSignatures();
  const { setAlert } = useContext(SnackbarContext);

  const {
    canEdit,
    answers,
    currentApplication,
    saveApplicationAnswer,
    signatures,
    updateSignatures,
  } = useContext(CurrentApplicationContext);

  const [loadWait, setLoadWait] = useState<boolean>(false);
  const [canvasReady, setCanvasReady] = useState<boolean>(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  // Drives UI updates - useRef used for logic checks to avoid excessive re-renders
  const [savingSignatureUI, setSavingSignatureUI] = useState(false);

  const questionAnswer = useMemo(() => answers[question.id], [answers, question.id]);
  const [saveFailed, setSaveFailed] = useState(false);
  const [saved, setSaved] = useState(false);

  // Refs for mutable values that don't trigger re-renders
  const savingSignatureRef = useRef(false);
  const hasUnsavedChangesRef = useRef(false);
  const isDrawingRef = useRef(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveSignatureRef = useRef<() => Promise<void>>(async () => {});
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const enableClearButton = useMemo(() => {
    return canvasReady;
  }, [canvasReady]);

  const setSavingSignature = useCallback((value: boolean) => {
    savingSignatureRef.current = value;
    setSavingSignatureUI(value);
  }, []);

  const setHasUnsavedChanges = useCallback((value: boolean) => {
    hasUnsavedChangesRef.current = value;
  }, []);

  const clearAutoSaveTimer = useCallback(() => {
    if (autoSaveTimerRef.current != null) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, []);

  const startDrawing = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      if (ctxRef.current == null || canvasRef.current == null || !canvasReady) return;

      clearAutoSaveTimer();
      isDrawingRef.current = true;
      const { left, top } = canvasRef.current.getBoundingClientRect();
      ctxRef.current.beginPath();
      ctxRef.current.moveTo(event.clientX - left, event.clientY - top);
    },
    [canvasReady, clearAutoSaveTimer]
  );

  const draw = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current || ctxRef.current == null || !canvasReady) {
        return;
      }
      const { left, top } = canvasRef.current!.getBoundingClientRect();
      ctxRef.current.lineTo(event.clientX - left, event.clientY - top);
      ctxRef.current.stroke();
    },
    [canvasReady]
  );

  const endDrawing = useCallback(() => {
    const ctx = ctxRef.current;
    if (ctx == null || !isDrawingRef.current) return;

    ctx.closePath();
    isDrawingRef.current = false;

    setHasUnsavedChanges(true); // UI update ONCE

    clearAutoSaveTimer();
    autoSaveTimerRef.current = setTimeout(() => {
      if (isDrawingRef.current || savingSignatureRef.current || !hasUnsavedChangesRef.current) {
        return;
      }

      void saveSignatureRef.current();
    }, 1000);
  }, [clearAutoSaveTimer, setHasUnsavedChanges]);

  useEffect(() => {
    if (canvasRef.current == null) return;

    const context: CanvasRenderingContext2D | null = canvasRef.current.getContext('2d');
    if (context == null) return;
    ctxRef.current = context;
  }, []);

  useEffect(() => {
    return () => {
      clearAutoSaveTimer();
    };
  }, [clearAutoSaveTimer]);

  useEffect(() => {
    setTimeout(() => {
      setLoadWait(true);
    }, 1000);
  }, [answers, question.parentQuestion]);

  const signatureData: SignatureQuestionResponse | null = useMemo(() => {
    if (questionAnswer == null || questionAnswer.responseInt == null || signatures == null) {
      return null;
    }

    return (
      signatures.find(
        (signature: SignatureQuestionResponse) =>
          signature.signatureApplicationRelationshipId === questionAnswer.responseInt
      ) ?? null
    );
  }, [questionAnswer, signatures]);

  const signedDateText = useMemo(() => {
    return signatureData?.date != null
      ? dayjs(signatureData.date).format('MM/DD/YYYY')
      : 'Not signed yet';
  }, [signatureData?.date]);

  useEffect(() => {
    if (loadWait && ctxRef.current != null) {
      if (signatureData != null && signatureData.imageUrl != null) {
        const myImage = new Image();
        myImage.src = signatureData.imageUrl;

        setTimeout(() => {
          ctxRef.current!.drawImage(myImage, 0, 0);
          setCanvasReady(true);
        }, 1000);
      } else {
        setCanvasReady(true);
      }
    }
  }, [loadWait, signatureData]);

  const isSignatureValid = useCallback(() => {
    if (canvasRef.current == null || ctxRef.current == null) return false;

    const imageData = ctxRef.current.getImageData(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height
    );
    const data = imageData.data;

    let drawnPixels = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 128) drawnPixels++;
    }

    return drawnPixels > 100;
  }, []);

  const clearPad = useCallback(async () => {
    if (ctxRef.current == null || canvasRef.current == null || locale == null) return;

    clearAutoSaveTimer();

    if (signatureData?.signatureApplicationRelationshipId == null) {
      ctxRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      return;
    }

    setSaved(false);
    setSavingSignature(true);
    try {
      await deleteSignatureRelationship(signatureData?.signatureApplicationRelationshipId);
      updateSignatures(
        (signatures ?? []).filter(
          (s) =>
            s.signatureApplicationRelationshipId !==
            signatureData.signatureApplicationRelationshipId
        )
      );

      if (currentApplication.id != null) {
        await saveApplicationAnswer(
          currentApplication.id,
          question.id,
          null,
          question.dataType,
          locale
        );
      }

      if (ctxRef.current != null && canvasRef.current != null) {
        ctxRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }

      setHasUnsavedChanges(false);
      setSavingSignature(false);
      setSaveFailed(false);
      setSaved(true);
    } catch {
      setAlert({ severity: 'error', message: 'Error deleting signature.' });
      setSaveFailed(true);
      setSaved(false);
    }
  }, [
    currentApplication.id,
    deleteSignatureRelationship,
    locale,
    question.dataType,
    question.id,
    saveApplicationAnswer,
    setAlert,
    setHasUnsavedChanges,
    setSavingSignature,
    clearAutoSaveTimer,
    signatureData?.signatureApplicationRelationshipId,
    signatures,
    updateSignatures,
  ]);

  const handleOpenClearConfirmation = useCallback(() => {
    setConfirmClearOpen(true);
  }, []);

  const handleCloseClearConfirmation = useCallback(() => {
    setConfirmClearOpen(false);
  }, []);

  const saveSignature = useCallback(async () => {
    if (canvasRef.current == null || locale == null) return;

    if (!isSignatureValid()) {
      setAlert({
        severity: 'error',
        message: 'Signature is too short. Please draw a longer signature.',
      });
      return;
    }

    setSavingSignature(true);

    const imageURL = canvasRef.current.toDataURL();

    const signatureSaveRequest: SignatureQuestionResponse = {
      ...signatureData,
      signatureApplicationRelationshipId: signatureData?.signatureApplicationRelationshipId ?? null,
      signatureId: signatureData?.signatureId ?? null,
      imageUrl: imageURL,
      date: dayjs().format('YYYY-MM-DD'),
      questionId: question.id,
      applicationId: currentApplication.id ?? null,
    };

    const result: SignatureQuestionResponse = await postSignature(signatureSaveRequest);

    if (result?.signatureId != null) {
      updateSignatures([...(signatures ?? []), result]);

      if (currentApplication.id != null) {
        try {
          await saveApplicationAnswer(
            currentApplication.id,
            question.id,
            result.signatureApplicationRelationshipId,
            question.dataType,
            locale
          );
        } catch {
          setAlert({ severity: 'error', message: 'Error saving signature.' });
          setSaveFailed(true);
        }

        setAlert({ severity: 'success', message: translatedText.signature.messageSaved });
      }
    }

    setSaveFailed(false);
    setSaved(true);
    setSavingSignature(false);
    setHasUnsavedChanges(false);
  }, [
    currentApplication.id,
    isSignatureValid,
    locale,
    postSignature,
    question.dataType,
    question.id,
    saveApplicationAnswer,
    setAlert,
    setHasUnsavedChanges,
    setSavingSignature,
    signatureData,
    signatures,
    translatedText.signature.messageSaved,
    updateSignatures,
  ]);

  useEffect(() => {
    saveSignatureRef.current = saveSignature;
  }, [saveSignature]);

  return !canEdit && loadWait ? (
    <>
      <p>
        <b>{question.questionText}</b>
      </p>
      <img alt='signature' src={signatureData?.imageUrl != null ? signatureData.imageUrl : ''} />
    </>
  ) : (
    <>
      <Box marginTop='25px' display='block' position='relative' id='signature-wrap'>
        <Box sx={{ textWrap: 'balance' }}>
          <b>
            {question.questionText} <RequiredIndicator />
          </b>
        </Box>
        <Box>
          {!canvasReady && (
            <Box position={'absolute'} top='100px' left='150px' zIndex={1000}>
              <Spinner type={'circular'} />
            </Box>
          )}
          <Box width='100%' display='flex' alignItems='center' flexDirection='row' gap='10px'>
            <form
              className='signature-pad-form'
              encType='multipart/form-data'
              id='e-signature-form'
            >
              <canvas
                ref={canvasRef}
                height={150}
                width={350}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={endDrawing}
                onMouseLeave={endDrawing}
                className='signature-pad'
                style={{
                  backgroundColor: theme.palette.sandTint.main,
                  border: `1px solid ${theme.palette.sandShade3.main}`,
                }}
              ></canvas>
            </form>
            <Box
              height='150px'
              display='flex'
              flexDirection='column'
              justifyContent='space-between'
            >
              <Box sx={{ alignItems: 'center', flexWrap: 'wrap', height: '100%', display: 'flex' }}>
                {savingSignatureUI ? (
                  <SavedIndicator
                    saved={saved}
                    saveFailed={saveFailed}
                    isSaving={savingSignatureRef.current}
                    saveFn={saveSignature}
                  />
                ) : (
                  <Button
                    variant='secondary'
                    size='small'
                    sx={{ marginLeft: 0 }}
                    onClick={handleOpenClearConfirmation}
                    disabled={!enableClearButton}
                  >
                    {translatedText.signature.buttonClear}
                  </Button>
                )}
              </Box>
              <Box width={'auto'} alignSelf={'end'}>
                <TextField
                  label='Signed Date'
                  value={signedDateText}
                  variant='outlined'
                  size='small'
                  InputProps={{ readOnly: true }}
                  sx={{ width: '180px' }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <ConfirmationModal
        open={confirmClearOpen}
        onClose={handleCloseClearConfirmation}
        header='Clear Signature'
        confirmationText='Are you sure you want to clear this signature? This action cannot be undone.'
        confirmButtonText='Clear'
        onDelete={clearPad}
      />
    </>
  );
}
